import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const SHORTCUT_MAP = {
  'g+d': '/dashboard',
  'g+c': '/contacts',
  'g+o': '/organizations',
  'g+t': '/tasks',
  'g+f': '/dossiers',
  'g+p': '/payments',
  'g+r': '/reporting',
  'g+s': '/settings',
};

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();

  const isInputFocused = useCallback(() => {
    const el = document.activeElement;
    if (!el) return false;
    const tag = el.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
  }, []);

  useEffect(() => {
    let gPressed = false;
    let gTimer = null;

    const goBack = () => {
      if (window.history.state?.idx > 0) {
        navigate(-1);
      } else {
        navigate('/');
      }
    };

    const handleKeyDown = (e) => {
      // Alt+ArrowLeft — always available (even when typing), like a browser back
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
        return;
      }

      // Escape — back to previous page (only when no input focused and no modal open)
      if (e.key === 'Escape' && !isInputFocused()) {
        const hasOpenModal = document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
        );
        if (!hasOpenModal) {
          e.preventDefault();
          goBack();
          return;
        }
      }

      // Skip if input is focused or modifier keys used (except for Ctrl+K which is handled by CommandPalette)
      if (isInputFocused()) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (!e.key) return;
      const key = e.key.toLowerCase();

      // 'g' prefix for navigation
      if (key === 'g' && !gPressed) {
        gPressed = true;
        gTimer = setTimeout(() => { gPressed = false; }, 800);
        return;
      }

      if (gPressed) {
        gPressed = false;
        if (gTimer) clearTimeout(gTimer);
        const combo = `g+${key}`;
        const path = SHORTCUT_MAP[combo];
        if (path) {
          e.preventDefault();
          navigate(path);
          return;
        }
      }

      // 'n' for new — dispatches a custom event picked up by the active page
      if (key === 'n') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('crm-shortcut-new'));
        return;
      }

      // '/' to focus search
      if (key === '/') {
        e.preventDefault();
        const evt = new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true });
        window.dispatchEvent(evt);
        return;
      }

      // '?' to show shortcuts help
      if (key === '?' && e.shiftKey) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('crm-shortcut-help'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (gTimer) clearTimeout(gTimer);
    };
  }, [navigate, location, isInputFocused]);
}
